import { motion } from 'framer-motion';
import { Eye, Cpu, Zap, ShieldCheck, TrendingUp } from 'lucide-react';

const steps = [
  {
    num: '01',
    icon: Eye,
    title: 'Sense',
    desc: 'Robots generate μTokens from vision, audio, tactile, and proprioception — every percept becomes a verifiable primitive.',
    accent: '#00F0FF',
  },
  {
    num: '02',
    icon: Cpu,
    title: 'Think',
    desc: 'Local policy selection in 200–800ms — no cloud dependency. Pure on-device inference with full auditability.',
    accent: '#4A90D9',
  },
  {
    num: '03',
    icon: Zap,
    title: 'Act',
    desc: 'Control Barrier Certificates enforce safety — every action is verified before execution. Provably safe autonomy.',
    accent: '#00F0FF',
  },
  {
    num: '04',
    icon: ShieldCheck,
    title: 'Verify',
    desc: 'Proof-of-Inference + optional zk — every decision is cryptographically verifiable, auditable, and insurable.',
    accent: '#4A90D9',
  },
  {
    num: '05',
    icon: TrendingUp,
    title: 'Reward',
    desc: 'cCOMP spend, nDATA-R licensing, iCTV royalties — closed-loop incentives for every contribution to the Superbrain.',
    accent: '#00F0FF',
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative py-28 md:py-36 overflow-hidden"
      style={{ background: '#0A0A0A' }}
    >
      {/* Grid background */}
      <div className="grid-bg absolute inset-0 opacity-60" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
        {/* Header */}
        <motion.div
          className="mb-16 md:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-mono text-[#00F0FF] text-xs tracking-[0.35em] uppercase mb-4">
            The Protocol
          </p>
          <h2
            className="font-display text-white font-bold tracking-tight"
            style={{ fontSize: 'clamp(36px, 5vw, 60px)', fontFamily: "'Syne', sans-serif" }}
          >
            How NeuroMesh Works
          </h2>
          <p className="text-[#666] mt-4 max-w-xl leading-relaxed">
            A five-layer protocol stack transforming raw robot perception into verifiable, monetizable intelligence.
          </p>
        </motion.div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.num}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ scale: 1.03, y: -6 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="glass-card card-3d spotlight p-7 flex flex-col gap-5 relative overflow-hidden group rounded-xl"
                data-testid={`step-card-${step.num}`}
              >
                {/* Step number watermark */}
                <span
                  className="font-display font-bold absolute top-3 right-5 opacity-[0.06] select-none"
                  style={{ fontSize: '64px', lineHeight: 1, color: step.accent }}
                >
                  {step.num}
                </span>

                {/* Icon */}
                <div
                  className="w-12 h-12 flex items-center justify-center relative"
                  style={{ border: `1px solid ${step.accent}30` }}
                >
                  <Icon size={20} color={step.accent} />
                  {/* Pulse ring */}
                  <motion.div
                    className="absolute inset-0 rounded-none"
                    animate={{ scale: [1, 1.4], opacity: [0.3, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeOut', delay: i * 0.4 }}
                    style={{ border: `1px solid ${step.accent}` }}
                  />
                </div>

                <div>
                  <h3
                    className="font-display text-white font-semibold text-lg mb-2"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-[#777] text-sm leading-relaxed">{step.desc}</p>
                </div>

                {/* Bottom accent line */}
                <div
                  className="absolute bottom-0 left-0 h-[1px] w-0 group-hover:w-full transition-all duration-500"
                  style={{ background: `linear-gradient(90deg, ${step.accent}, transparent)` }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
